import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CorporateFareTwoTone,
  KeyTwoTone,
  PersonTwoTone,
  PublicTwoTone,
  ShieldTwoTone,
} from '@mui/icons-material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  getEffectiveScope,
  getScopesForResource,
  hasPermission,
} from './permissionUtils'

import { camelToLabel } from '../../utilities/stringUtils'
import { usePermissionDraft } from './usePermissionDraft'
import { visuallyHidden } from '@mui/utils'

const LABEL_COL_WIDTH = 100

const ACTION_ORDER = [
  'create',
  'read',
  'update',
  'softDelete',
  'delete',
  'restore',
  'purge',
]

const SCOPE_ICONS = {
  all: PublicTwoTone,
  organization: CorporateFareTwoTone,
  assigned: KeyTwoTone,
  own: PersonTwoTone,
}

function ScopeIcon({ scope }) {
  const Icon = SCOPE_ICONS[scope] ?? ShieldTwoTone
  return (
    <Tooltip title={camelToLabel(scope)}>
      <Icon fontSize="small" />
    </Tooltip>
  )
}

function buildSingleRoleGrid(permissions, permissionOptions) {
  const perms = permissions ?? []

  if (permissionOptions) {
    const resources = permissionOptions.resources.toSorted()
    const actions = ACTION_ORDER.filter((a) =>
      permissionOptions.actions.includes(a),
    )
    return { resources, actions }
  }

  const resourceSet = new Set()
  const actionSet = new Set()
  for (const p of perms) {
    resourceSet.add(p.resource)
    actionSet.add(p.action)
  }
  const resources = [...resourceSet].toSorted()
  const actions = ACTION_ORDER.filter((a) => actionSet.has(a))
  return { resources, actions }
}

function ScopeCell({
  isEditMode,
  resource,
  currentScope,
  scopesForResource,
  permissionOptions,
  onScopeChange,
}) {
  if (isEditMode) {
    return (
      <TableCell align="center">
        <FormControl size="small" fullWidth>
          <InputLabel
            id={`scope-selector-label-${resource}`}
            htmlFor={`scope-selector-input-${resource}`}
            sx={visuallyHidden}
          >
            {`${camelToLabel(resource)} scope`}
          </InputLabel>
          <Select
            size="small"
            fullWidth
            labelId={`scope-selector-label-${resource}`}
            inputProps={{ id: `scope-selector-input-${resource}` }}
            value={currentScope}
            displayEmpty
            onChange={(e) => onScopeChange(resource, e.target.value)}
          >
            {(permissionOptions?.scopes ?? []).map((scope) => (
              <MenuItem key={scope} value={scope}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScopeIcon scope={scope} />
                  {camelToLabel(scope)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
    )
  }
  return (
    <TableCell align="center">
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
        {scopesForResource.length > 0 ? (
          scopesForResource.map((s) => <ScopeIcon key={s} scope={s} />)
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </Box>
    </TableCell>
  )
}

function ActionCell({ isEditMode, action, resource, granted, onToggle }) {
  const theme = useTheme()
  const grantedBg = alpha(theme.palette.success.main, 0.15)
  const deniedBg = alpha(theme.palette.error.main, 0.08)

  if (isEditMode) {
    return (
      <TableCell
        align="center"
        sx={{ p: 0.5, bgcolor: granted ? grantedBg : deniedBg }}
      >
        <Checkbox
          size="small"
          inputProps={{ 'aria-label': `${resource}-${action}` }}
          checked={granted}
          onChange={() => onToggle(resource, action)}
          color="success"
        />
      </TableCell>
    )
  }
  return (
    <TableCell
      align="center"
      sx={{
        bgcolor: granted ? grantedBg : deniedBg,
        fontWeight: granted ? 700 : 'normal',
        color: granted ? 'success.main' : 'text.disabled',
      }}
    >
      {granted ? '✓' : ''}
    </TableCell>
  )
}

const PermissionMatrix = ({ permissions, permissionOptions, onChange }) => {
  const theme = useTheme()
  const { perms, pendingScopes, isEditMode, handleToggle, handleScopeChange } =
    usePermissionDraft(permissions, permissionOptions, onChange)

  const { resources, actions } = buildSingleRoleGrid(
    perms,
    isEditMode ? permissionOptions : null,
  )

  if (resources.length === 0 && actions.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No permissions assigned</Typography>
      </Box>
    )
  }

  const stickyLabelSx = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    bgcolor: 'background.paper',
    borderRight: `1px solid ${theme.palette.divider}`,
    width: LABEL_COL_WIDTH,
    minWidth: LABEL_COL_WIDTH,
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...stickyLabelSx,
                zIndex: 3,
                bgcolor: 'background.paper',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              Resource
            </TableCell>
            <TableCell
              align="center"
              sx={{
                ...stickyLabelSx,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              Scope
            </TableCell>
            {actions.map((action) => (
              <TableCell
                key={action}
                align="center"
                sx={{
                  bgcolor: 'background.paper',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {camelToLabel(action)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {resources.map((resource, rowIdx) => {
            const rowBg =
              rowIdx % 2 === 0 ? 'background.paper' : 'background.default'
            const scopesForResource = getScopesForResource(perms, resource)
            const currentScope = getEffectiveScope(
              scopesForResource,
              pendingScopes,
              resource,
              permissionOptions,
            )

            return (
              <TableRow key={resource} sx={{ bgcolor: rowBg }}>
                <TableCell
                  sx={{
                    ...stickyLabelSx,
                    bgcolor: rowBg,
                    color: 'text.primary',
                  }}
                >
                  {camelToLabel(resource)}
                </TableCell>

                <ScopeCell
                  isEditMode={isEditMode}
                  resource={resource}
                  currentScope={currentScope}
                  scopesForResource={scopesForResource}
                  permissionOptions={permissionOptions}
                  onScopeChange={handleScopeChange}
                />

                {actions.map((action) => (
                  <ActionCell
                    key={action}
                    isEditMode={isEditMode}
                    action={action}
                    resource={resource}
                    granted={hasPermission(perms, resource, action)}
                    onToggle={handleToggle}
                  />
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default PermissionMatrix
